import { create } from 'zustand'
import { Settings } from 'luxon'
import { sortBy } from 'lodash'
import { formatTranslationMessages, fillGaps } from '../../i18n'
import { DEFAULT_LOCALE } from '../../containers/app/constants'
import {
  messages as enMessages,
  countryNames as enCountryNames
} from './chunks/en'
import { shallow } from 'zustand/shallow'
// don't remove this import
import { store } from '../../redux-store'

export const getCountryNamesAsOptions = () => {
  const countryNames = { ...useLanguageStore.getState().countryNames }
  delete countryNames.default

  return sortBy(
    Object.entries(countryNames).map(([code, name]) => ({
      label: name,
      value: code
    })),
    ['label']
  )
}

// getting language from sessionStorage
const getStoredLocale = () => {
  if (typeof sessionStorage !== 'undefined') {
    return sessionStorage.getItem('language') || DEFAULT_LOCALE
  }
  return DEFAULT_LOCALE
}

// Função para carregar os chunks de idioma dinamicamente
const importLanguageChunk = async (locale) => {
  switch (locale) {
    case 'en':
      return import('./chunks/en')
    case 'de':
      return import('./chunks/de')
    case 'pl':
      return import('./chunks/pl')
    case 'cs':
      return import('./chunks/cs')
    case 'bg':
      return import('./chunks/bg')
    case 'da':
      return import('./chunks/da')
    case 'fr':
      return import('./chunks/fr')
    case 'hu':
      return import('./chunks/hu')
    case 'sk':
      return import('./chunks/sk')
    case 'tr':
      return import('./chunks/tr')
    case 'pt':
      return import('./chunks/pt')
    case 'it':
      return import('./chunks/it')
    case 'es':
      return import('./chunks/es')
    case 'sr':
      return import('./chunks/sr')
    case 'sv':
      return import('./chunks/sv')
    case 'hr':
      return import('./chunks/hr')
    case 'ru':
      return import('./chunks/ru')
    case 'ro':
      return import('./chunks/ro')
    case 'sl':
      return import('./chunks/sl')
    case 'nl':
      return import('./chunks/nl')
    case 'no':
      return import('./chunks/no')
    default:
      return import('./chunks/en')
  }
}

// Middleware
const languageStoreMiddleware = (config) => (set, get, api) => {
  const originalChangeLocale = config(set, get, api).changeLocale

  return {
    ...config(set, get, api),

    changeLocale: async (locale) => {
      sessionStorage.setItem('language', locale)
      await originalChangeLocale(locale)
    }
  }
}

export const useLanguageStore = create(
  languageStoreMiddleware((set, get) => ({
    locale: getStoredLocale(),
    messages: enMessages,
    countryNames: enCountryNames,
    localeChanged: false,
    setLocaleChanged: (localeChanged) => set({ localeChanged }),

    changeLocale: async (locale) => {
      const { countryNames, messages } = await importLanguageChunk(locale)
      Settings.defaultLocale = locale

      const formattedMessages = fillGaps(
        formatTranslationMessages(locale, messages)
      )

      set({
        locale,
        localeChanged: true,
        messages: formattedMessages,
        countryNames
      })
    },

    logout: () => {
      sessionStorage.removeItem('language')
      set({ localeChanged: false })
    }
  }))
)

// Selectors
export const useCountryNames = () => {
  const countryNames = useLanguageStore(
    (state) => state.countryNames ?? [],
    shallow
  )

  const defineCountryNames = countryNames.default ?? countryNames

  if (defineCountryNames) {
    const options = Object.entries(defineCountryNames).map(([code, name]) => {
      return {
        label: name,
        value: code
      }
    })
    return sortBy(options, ['label'])
  }

  return []
}

export const getLocaleChangedStatic = () =>
  useLanguageStore.getState().localeChanged ?? null
export const getChangeLocaleStatic = () =>
  useLanguageStore.getState().changeLocale ?? null
